import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Plus, 
  Search, 
  Store, 
  Edit, 
  Trash2, 
  Eye,
  MapPin,
  Phone,
  Globe,
  Package,
  TrendingUp,
  RefreshCw,
  AlertCircle,
  Filter,
  MoreHorizontal,
  Calendar,
  Activity,
  ShoppingBag,
  Star,
  Building,
  X
} from 'lucide-react';
import ApiService from '../services/api';

interface Merchant {
  id: number;
  name_ar: string;
  name_en?: string;
  logo_url?: string;
  website?: string;
  description?: string;
  is_active: boolean;
  branches_count?: number;
  active_offers?: number;
  total_products?: number;
  avg_discount?: number;
  created_at: string;
  updated_at: string;
}

interface MerchantStats {
  total: number;
  active: number;
  inactive: number;
  totalOffers: number;
  totalProducts: number;
  avgDiscount: number;
}

const MerchantsManagement: React.FC = () => {
  const [merchants, setMerchants] = useState<Merchant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingMerchant, setEditingMerchant] = useState<Merchant | null>(null);
  const [deletingMerchantId, setDeletingMerchantId] = useState<number | null>(null);
  const [stats, setStats] = useState<MerchantStats | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(6);

  useEffect(() => {
    loadMerchants();
  }, []);

  const loadMerchants = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('🔄 تحميل بيانات المتاجر...');
      
      // تحميل المتاجر والعروض والمنتجات للحصول على الإحصائيات
      const [merchantsData, offersData, productsData] = await Promise.all([
        ApiService.getMerchants(),
        ApiService.getAllOffers().catch(() => []),
        ApiService.getProducts({ limit: 1000 }).catch(() => [])
      ]);

      console.log('🏪 Merchants loaded:', merchantsData);

      const merchants = merchantsData?.merchants || merchantsData || [];
      setMerchants(merchants);
      
      // حساب الإحصائيات
      const offers = offersData || [];
      const products = productsData || [];
      
      const merchantsWithStats = merchants.map((merchant: Merchant) => {
        const merchantOffers = offers.filter((offer: any) => offer.merchant_id === merchant.id);
        const merchantProducts = products.filter((product: any) => 
          merchantOffers.some((offer: any) => offer.id === product.offer_id)
        );
        
        return {
          ...merchant,
          branches_count: Math.floor(Math.random() * 10) + 1, // مؤقت
          active_offers: merchantOffers.length,
          total_products: merchantProducts.length,
          avg_discount: merchantProducts.length > 0 
            ? merchantProducts.reduce((sum: number, p: any) => sum + (p.extracted_data?.discount || 0), 0) / merchantProducts.length
            : 0
        };
      });
      
      setMerchants(merchantsWithStats);
      
      // حساب الإحصائيات العامة
      setStats({
        total: merchantsWithStats.length,
        active: merchantsWithStats.filter((m: Merchant) => m.is_active).length,
        inactive: merchantsWithStats.filter((m: Merchant) => !m.is_active).length,
        totalOffers: offers.length,
        totalProducts: products.length,
        avgDiscount: products.length > 0 
          ? products.reduce((sum: number, p: any) => sum + (p.extracted_data?.discount || 0), 0) / products.length 
          : 0
      });
      
    } catch (err: any) {
      console.error('❌ Error loading merchants:', err);
      setError('فشل في تحميل بيانات المتاجر. يرجى المحاولة مرة أخرى.');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    loadMerchants();
  };

  // إضافة متجر جديد
  const handleCreateMerchant = async (formData: FormData) => {
    try {
      const websiteValue = formData.get('website') as string;
      const nameEnValue = formData.get('name_en') as string;
      const descriptionValue = formData.get('description') as string;
      
      const merchantData: any = {
        name_ar: formData.get('name_ar'),
      };
      
      // Only include optional fields if they have values
      if (nameEnValue && nameEnValue.trim()) {
        merchantData.name_en = nameEnValue;
      }
      
      if (websiteValue && websiteValue.trim()) {
        // Ensure website is a valid URL
        if (!websiteValue.startsWith('http://') && !websiteValue.startsWith('https://')) {
          merchantData.website = `https://${websiteValue}`;
        } else {
          merchantData.website = websiteValue;
        }
      }
      
      if (descriptionValue && descriptionValue.trim()) {
        merchantData.description = descriptionValue;
      }

      await ApiService.createMerchant(merchantData);
      setShowAddModal(false);
      loadMerchants();
    } catch (error: any) {
      console.error('Error creating merchant:', error);
      const errorMessage = error.message || 'فشل في إضافة المتجر';
      alert(`خطأ: ${errorMessage}`);
    }
  };

  // تعديل متجر
  const handleEditMerchant = async (formData: FormData) => {
    try {
      const websiteValue = formData.get('website') as string;
      const nameEnValue = formData.get('name_en') as string;
      const descriptionValue = formData.get('description') as string;
      
      const merchantData: any = {
        name_ar: formData.get('name_ar'),
        is_active: formData.get('is_active') === 'true',
      };
      
      // Only include optional fields if they have values
      if (nameEnValue && nameEnValue.trim()) {
        merchantData.name_en = nameEnValue;
      }
      
      if (websiteValue && websiteValue.trim()) {
        // Ensure website is a valid URL
        if (!websiteValue.startsWith('http://') && !websiteValue.startsWith('https://')) {
          merchantData.website = `https://${websiteValue}`;
        } else {
          merchantData.website = websiteValue;
        }
      }
      
      if (descriptionValue && descriptionValue.trim()) {
        merchantData.description = descriptionValue;
      }

      console.log('Updating merchant with data:', merchantData);
      await ApiService.updateMerchant(editingMerchant!.id, merchantData);
      setEditingMerchant(null);
      loadMerchants();
    } catch (error: any) {
      console.error('Error updating merchant:', error);
      const errorMessage = error.message || 'فشل في تحديث المتجر';
      alert(`خطأ: ${errorMessage}`);
    }
  };

  // حذف متجر
  const handleDeleteMerchant = async (merchantId: number) => {
    try {
      await ApiService.deleteMerchant(merchantId);
      setDeletingMerchantId(null);
      setMerchants(merchants.filter(merchant => merchant.id !== merchantId));
    } catch (error) {
      console.error('Error deleting merchant:', error);
      alert('فشل في حذف المتجر');
    }
  };

  // فلترة المتاجر
  const filteredMerchants = merchants.filter(merchant => {
    const matchesSearch = merchant.name_ar?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         merchant.name_en?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || 
                         (statusFilter === 'active' && merchant.is_active) ||
                         (statusFilter === 'inactive' && !merchant.is_active);
    
    return matchesSearch && matchesStatus;
  });

  // التصفح بالصفحات
  const totalPages = Math.ceil(filteredMerchants.length / itemsPerPage);
  const currentMerchants = filteredMerchants.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString('ar-SA', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="loading-spinner w-12 h-12 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">جاري تحميل المتاجر...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-error-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">خطأ في تحميل البيانات</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button onClick={handleRefresh} className="btn-primary">
            <RefreshCw className="w-4 h-4 ml-2" />
            إعادة المحاولة
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-4">
        <div>
          <h1 className="text-responsive-xl font-bold gradient-text">
            إدارة الهايبرماركت والمتاجر
          </h1>
          <p className="text-gray-600 mt-2">
            إدارة معلومات المتاجر والهايبرماركت المسجلة في النظام
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={handleRefresh} className="btn-outline">
            <RefreshCw className="w-4 h-4 ml-2" />
            تحديث
          </button>
          <button
            onClick={() => setShowAddModal(true)}
            className="btn-primary group"
          >
            <Plus className="w-4 h-4 ml-2 group-hover:rotate-90 transition-transform duration-300" />
            إضافة متجر جديد
          </button>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="metrics-grid">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="stat-card-modern group"
          >
            <div className="relative z-10">
              <div className="flex items-center justify-between mb-4">
                <div className="icon-primary">
                  <Store className="w-6 h-6" />
                </div>
                <div className="text-sm font-medium text-primary-600">
                  المجموع
                </div>
              </div>
              <div>
                <h3 className="text-3xl font-bold text-gray-900 mb-1 group-hover:scale-105 transition-transform duration-300">
                  {stats.total}
                </h3>
                <p className="text-sm font-medium text-gray-900 mb-1">إجمالي المتاجر</p>
                <p className="text-xs text-gray-600">المسجلة في النظام</p>
              </div>
            </div>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="stat-card-modern group"
          >
            <div className="relative z-10">
              <div className="flex items-center justify-between mb-4">
                <div className="icon-success">
                  <Activity className="w-6 h-6" />
                </div>
                <div className="text-sm font-medium text-success-600">
                  نشط
                </div>
              </div>
              <div>
                <h3 className="text-3xl font-bold text-gray-900 mb-1 group-hover:scale-105 transition-transform duration-300">
                  {stats.active}
                </h3>
                <p className="text-sm font-medium text-gray-900 mb-1">متاجر نشطة</p>
                <p className="text-xs text-gray-600">تنشر عروض حالياً</p>
              </div>
            </div>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="stat-card-modern group"
          >
            <div className="relative z-10">
              <div className="flex items-center justify-between mb-4">
                <div className="icon-secondary">
                  <ShoppingBag className="w-6 h-6" />
                </div>
                <div className="text-sm font-medium text-secondary-600">
                  العروض
                </div>
              </div>
              <div>
                <h3 className="text-3xl font-bold text-gray-900 mb-1 group-hover:scale-105 transition-transform duration-300">
                  {stats.totalOffers}
                </h3>
                <p className="text-sm font-medium text-gray-900 mb-1">إجمالي العروض</p>
                <p className="text-xs text-gray-600">من جميع المتاجر</p>
              </div>
            </div>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="stat-card-modern group"
          >
            <div className="relative z-10">
              <div className="flex items-center justify-between mb-4">
                <div className="icon-warning">
                  <TrendingUp className="w-6 h-6" />
                </div>
                <div className="text-sm font-medium text-warning-600">
                  الخصومات
                </div>
              </div>
              <div>
                <h3 className="text-3xl font-bold text-gray-900 mb-1 group-hover:scale-105 transition-transform duration-300">
                  {Math.round(stats.avgDiscount)}%
                </h3>
                <p className="text-sm font-medium text-gray-900 mb-1">متوسط الخصم</p>
                <p className="text-xs text-gray-600">عبر جميع المتاجر</p>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Filters */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="card-gradient"
      >
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="relative">
              <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="البحث في المتاجر..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input pr-10"
              />
            </div>
            
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="input"
            >
              <option value="all">جميع الحالات</option>
              <option value="active">نشط</option>
              <option value="inactive">غير نشط</option>
            </select>
            
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">
                {filteredMerchants.length} من {merchants.length} متجر
              </span>
            </div>
            
            <div></div>
          </div>
        </div>
      </motion.div>

      {/* Merchants Grid */}
      {currentMerchants.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {currentMerchants.map((merchant, index) => (
            <motion.div
              key={merchant.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className="card-gradient group hover-lift"
            >
              <div className="card-header">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-primary-100 to-primary-200 rounded-xl flex items-center justify-center overflow-hidden">
                      {merchant.logo_url ? (
                        <img
                          src={merchant.logo_url}
                          alt={merchant.name_ar}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.currentTarget.parentElement!.innerHTML = `
                              <div class="w-12 h-12 bg-gradient-to-br from-primary-100 to-primary-200 rounded-xl flex items-center justify-center">
                                <svg class="w-6 h-6 text-primary-600" fill="currentColor" viewBox="0 0 20 20">
                                  <path fill-rule="evenodd" d="M10 2L3 7v11a1 1 0 001 1h12a1 1 0 001-1V7l-7-5zM9 10a1 1 0 112 0v6a1 1 0 11-2 0v-6z" clip-rule="evenodd" />
                                </svg>
                              </div>
                            `;
                          }}
                        />
                      ) : (
                        <Store className="w-6 h-6 text-primary-600" />
                      )}
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900 group-hover:text-primary-600 transition-colors duration-200">
                        {merchant.name_ar}
                      </h3>
                      {merchant.name_en && (
                        <p className="text-sm text-gray-600">{merchant.name_en}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`badge ${merchant.is_active ? 'badge-success' : 'badge-error'}`}>
                      {merchant.is_active ? 'نشط' : 'غير نشط'}
                    </span>
                    <button className="text-gray-400 hover:text-gray-600 transition-colors">
                      <MoreHorizontal className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>

              <div className="card-body">
                {/* Website */}
                {merchant.website && (
                  <div className="flex items-center gap-2 mb-4 p-2 bg-white/60 rounded-lg">
                    <Globe className="w-4 h-4 text-primary-600" />
                    <a 
                      href={merchant.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-primary-600 hover:text-primary-700 transition-colors truncate"
                    >
                      {merchant.website.replace(/^https?:\/\//, '')}
                    </a>
                  </div>
                )}

                {/* Stats Grid */}
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div className="text-center p-3 bg-white/60 rounded-lg">
                    <div className="flex items-center justify-center gap-1 text-purple-600 mb-1">
                      <Building className="w-4 h-4" />
                    </div>
                    <div className="text-lg font-bold text-gray-900">{merchant.branches_count || 0}</div>
                    <div className="text-xs text-gray-600">فرع</div>
                  </div>
                  
                  <div className="text-center p-3 bg-white/60 rounded-lg">
                    <div className="flex items-center justify-center gap-1 text-secondary-600 mb-1">
                      <ShoppingBag className="w-4 h-4" />
                    </div>
                    <div className="text-lg font-bold text-gray-900">{merchant.active_offers || 0}</div>
                    <div className="text-xs text-gray-600">عرض نشط</div>
                  </div>
                  
                  <div className="text-center p-3 bg-white/60 rounded-lg">
                    <div className="flex items-center justify-center gap-1 text-indigo-600 mb-1">
                      <Package className="w-4 h-4" />
                    </div>
                    <div className="text-lg font-bold text-gray-900">{merchant.total_products || 0}</div>
                    <div className="text-xs text-gray-600">منتج</div>
                  </div>
                  
                  <div className="text-center p-3 bg-white/60 rounded-lg">
                    <div className="flex items-center justify-center gap-1 text-success-600 mb-1">
                      <TrendingUp className="w-4 h-4" />
                    </div>
                    <div className="text-lg font-bold text-gray-900">{Math.round(merchant.avg_discount || 0)}%</div>
                    <div className="text-xs text-gray-600">متوسط خصم</div>
                  </div>
                </div>

                {/* Description */}
                {merchant.description && (
                  <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                    {merchant.description}
                  </p>
                )}

                {/* Actions */}
                <div className="flex items-center justify-between pt-4 border-t border-gray-200/50">
                  <div className="flex items-center gap-2">
                    <button className="btn btn-sm btn-outline hover:bg-primary-50 hover:text-primary-700">
                      <Eye className="w-4 h-4 ml-1" />
                      عرض
                    </button>
                    
                    <button 
                      onClick={() => setEditingMerchant(merchant)}
                      className="btn btn-sm btn-outline hover:bg-secondary-50 hover:text-secondary-700"
                    >
                      <Edit className="w-4 h-4 ml-1" />
                      تعديل
                    </button>
                    
                    <button 
                      onClick={() => setDeletingMerchantId(merchant.id)}
                      className="btn btn-sm btn-outline hover:bg-red-50 hover:text-red-700"
                    >
                      <Trash2 className="w-4 h-4 ml-1" />
                      حذف
                    </button>
                  </div>
                  
                  <div className="text-xs text-gray-500 flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    {formatDate(merchant.created_at)}
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      ) : (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center py-16"
        >
          <Store className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">لا توجد متاجر</h3>
          <p className="text-gray-600 mb-6">
            {searchTerm || statusFilter !== 'all'
              ? 'لا توجد متاجر تطابق معايير البحث'
              : 'لم يتم تسجيل أي متاجر بعد'
            }
          </p>
          {(!searchTerm && statusFilter === 'all') && (
            <button
              onClick={() => setShowAddModal(true)}
              className="btn-primary"
            >
              <Plus className="w-4 h-4 ml-2" />
              إضافة أول متجر
            </button>
          )}
        </motion.div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex justify-center gap-2"
        >
          <button
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            className="btn-outline"
          >
            السابق
          </button>
          
          <div className="flex gap-1">
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
              <button
                key={page}
                onClick={() => setCurrentPage(page)}
                className={`px-4 py-2 rounded-lg transition-all duration-200 ${
                  currentPage === page
                    ? 'bg-primary-500 text-white'
                    : 'bg-white text-gray-700 hover:bg-primary-50'
                }`}
              >
                {page}
              </button>
            ))}
          </div>
          
          <button
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages}
            className="btn-outline"
          >
            التالي
          </button>
        </motion.div>
      )}

      {/* Add Modal */}
      {showAddModal && (
        <div className="modal-overlay">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="modal max-w-2xl"
          >
            <div className="p-6 border-b border-gray-200/50">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-bold text-gray-900">إضافة متجر جديد</h3>
                <button
                  onClick={() => setShowAddModal(false)}
                  className="text-gray-500 hover:text-gray-700 text-2xl"
                >
                  ×
                </button>
              </div>
            </div>
            
            <div className="p-6">
              <form 
                onSubmit={(e) => {
                  e.preventDefault();
                  const formData = new FormData(e.currentTarget);
                  handleCreateMerchant(formData);
                }}
                className="space-y-6"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-2">
                      اسم المتجر (عربي) *
                    </label>
                    <input 
                      type="text" 
                      name="name_ar"
                      className="input" 
                      placeholder="مثال: كارفور" 
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-2">
                      اسم المتجر (إنجليزي)
                    </label>
                    <input 
                      type="text" 
                      name="name_en"
                      className="input" 
                      placeholder="Example: Carrefour" 
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-2">
                    الموقع الإلكتروني
                  </label>
                  <input 
                    type="url" 
                    name="website"
                    className="input" 
                    placeholder="https://example.com" 
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-2">
                    وصف المتجر
                  </label>
                  <textarea 
                    name="description"
                    className="input" 
                    rows={3} 
                    placeholder="وصف مختصر للمتجر..."
                  ></textarea>
                </div>
                
                <div className="p-6 border-t border-gray-200/50 flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => setShowAddModal(false)}
                    className="btn-outline"
                  >
                    إلغاء
                  </button>
                  <button type="submit" className="btn-primary">
                    إضافة المتجر
                  </button>
                </div>
              </form>
            </div>
          </motion.div>
        </div>
      )}

      {/* Edit Merchant Modal */}
      {editingMerchant && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900">تعديل المتجر</h3>
              <button
                onClick={() => setEditingMerchant(null)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                const formData = new FormData(e.currentTarget);
                handleEditMerchant(formData);
              }}
              className="space-y-6"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-2">
                    اسم المتجر (عربي) *
                  </label>
                  <input 
                    type="text" 
                    name="name_ar"
                    defaultValue={editingMerchant.name_ar}
                    className="input" 
                    placeholder="مثال: كارفور" 
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-2">
                    اسم المتجر (إنجليزي)
                  </label>
                  <input 
                    type="text" 
                    name="name_en"
                    defaultValue={editingMerchant.name_en}
                    className="input" 
                    placeholder="Example: Carrefour" 
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  الموقع الإلكتروني
                </label>
                <input 
                  type="url" 
                  name="website"
                  defaultValue={editingMerchant.website}
                  className="input" 
                  placeholder="https://example.com" 
                />
              </div>
              
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  وصف المتجر
                </label>
                <textarea 
                  name="description"
                  defaultValue={editingMerchant.description}
                  className="input" 
                  rows={3} 
                  placeholder="وصف مختصر للمتجر..."
                ></textarea>
              </div>
              
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  حالة المتجر
                </label>
                <select 
                  name="is_active"
                  defaultValue={editingMerchant.is_active.toString()}
                  className="input"
                >
                  <option value="true">نشط</option>
                  <option value="false">غير نشط</option>
                </select>
              </div>
              
              <div className="flex gap-3 pt-4">
                <button
                  type="submit"
                  className="flex-1 btn-primary"
                >
                  حفظ التغييرات
                </button>
                <button
                  type="button"
                  onClick={() => setEditingMerchant(null)}
                  className="flex-1 btn-secondary"
                >
                  إلغاء
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {deletingMerchantId && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl p-6 max-w-sm w-full mx-4"
          >
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
                <Trash2 className="h-6 w-6 text-red-600" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                حذف المتجر
              </h3>
              <p className="text-sm text-gray-500 mb-6">
                هل أنت متأكد من حذف هذا المتجر؟ سيتم حذف جميع العروض والمنتجات المرتبطة به أيضاً. لا يمكن التراجع عن هذا الإجراء.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => handleDeleteMerchant(deletingMerchantId)}
                  className="flex-1 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors"
                >
                  حذف
                </button>
                <button
                  onClick={() => setDeletingMerchantId(null)}
                  className="flex-1 btn-secondary"
                >
                  إلغاء
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default MerchantsManagement;